import type { EntradaDelHistorial } from '@/types/historial.types';
import {
  creacionSinAutor,
  detalleDe,
  edicionEnPalabras,
  enMinuscula,
  quienLoHizo,
  sujeto,
  type CampoEditado,
  type EntradaEnPalabras,
} from '@/features/smart-pricing/lib/historial-de-estrategia-en-palabras';
import type { Action, RuleScope } from '../types/pricing-rules.types';
import {
  alcanceEnFrase,
  camposEnPalabras,
  describirReglaCompleta,
  fechaDeCalendario,
} from './reglas-en-palabras';

/**
 * Spec 294 (D-R4-5): una entrada del historial de una regla, en palabras. Cada
 * cambio aplicado dice quién lo escribió y quién lo aplicó; un cambio sin
 * aplicar que se descartó, quién lo descartó. Nunca un nombre de campo interno.
 */

type Foto = Record<string, unknown>;
const esObjeto = (v: unknown): v is Foto => typeof v === 'object' && v !== null;

/** El orden de la ficha de la regla (el de `CAMPOS_DE_LA_REGLA` del servidor). */
const ORDEN = [
  'name',
  'description',
  'isActive',
  'priority',
  'onStop',
  'scope',
  'conditions',
  'actions',
  'validFrom',
  'validUntil',
  'notes',
  'tags',
];

const texto = (v: unknown, vacio: string): string | null =>
  v === null || v === '' ? vacio : typeof v === 'string' ? `«${v}»` : null;

/** El valor de un campo, en palabras; null = no se dice (sólo se nombra el campo). */
function valorEnPalabras(campo: string, v: unknown): string | null {
  switch (campo) {
    case 'name':
      return typeof v === 'string' ? `«${v}»` : null;
    case 'actions':
      return Array.isArray(v) ? enMinuscula(describirReglaCompleta(v as Action[])) : null;
    case 'scope':
      return esObjeto(v) ? alcanceEnFrase(v as RuleScope) : null;
    case 'isActive':
      return v === true ? 'encendida' : v === false ? 'apagada' : null;
    // Ronda final de R4: días de calendario guardados `YYYY-MM-DDT00:00:00Z`; en
    // hora de Bolivia se leían un día antes. (La fecha de fin de una estrategia
    // sí es un instante: ésa sigue en Bolivia.)
    case 'validUntil':
      return v === null ? 'sin fecha de fin' : typeof v === 'string' ? fechaDeCalendario(v) : null;
    case 'validFrom':
      return v === null
        ? 'sin fecha de inicio'
        : typeof v === 'string'
          ? fechaDeCalendario(v)
          : null;
    case 'description':
      return texto(v, 'sin descripción');
    case 'notes':
      return texto(v, 'sin notas');
    case 'tags':
      return Array.isArray(v)
        ? v.length === 0
          ? 'ninguna'
          : v.map((t) => `«${String(t)}»`).join(', ')
        : null;
    default:
      // El orden, qué hace si no hay precio posible, los requisitos: se nombran solos.
      return null;
  }
}

function camposEditados(e: EntradaDelHistorial): CampoEditado[] {
  const antes = esObjeto(e.antes) ? e.antes : {};
  const despues = esObjeto(e.despues) ? e.despues : {};
  const cambiados = ORDEN.filter((c) => c in antes || c in despues);
  return cambiados.map((c) => ({
    campo: camposEnPalabras([c]),
    antes: c in antes ? valorEnPalabras(c, antes[c]) : null,
    despues: c in despues ? valorEnPalabras(c, despues[c]) : null,
  }));
}

/** « · la aplicó Roberto Méndez (también la tocó otra persona)». */
function quienLaAplico(e: EntradaDelHistorial): string {
  const d = detalleDe(e);
  const partes: string[] = [];
  // Ronda final de R4: si la aplicó la misma persona que la escribió, no se repite.
  const misma = e.aplicadoPor === e.quien && e.aplicadoPorSoporte === e.quienEsSoporte;
  if (e.aplicadoPor && !misma)
    partes.push(
      ` · la aplicó ${e.aplicadoPor}${e.aplicadoPorSoporte ? ', del soporte de Prixia' : ''}`
    );
  // `escribieron` son ids: los otros no se pueden nombrar, se cuentan.
  const escribieron = Array.isArray(d.escribieron) ? new Set(d.escribieron).size : 0;
  const otros = escribieron - 1;
  if (otros === 1) partes.push(' (también la tocó otra persona)');
  else if (otros > 1) partes.push(` (también la tocaron ${otros} personas más)`);
  return partes.join('');
}

/** Lo que cambiaba un cambio sin aplicar que se descartó. */
function queCambiaba(d: Foto): string | null {
  if (d.tipo === 'NUEVA') return 'Era una regla nueva.';
  if (d.tipo === 'BORRAR') return 'La borraba.';
  if (d.tipo === 'CAMBIO') {
    const campos = Array.isArray(d.campos) ? d.campos.map(String) : [];
    return `Cambiaba ${camposEnPalabras(campos)}.`;
  }
  return null;
}

export function historialDeReglaEnPalabras(e: EntradaDelHistorial): EntradaEnPalabras {
  const quien = sujeto(e);
  const aplico = quienLaAplico(e);
  switch (e.accion) {
    case 'CREATED':
      return creacionSinAutor(e) ?? { titulo: `${quien} la creó${aplico}`, lineas: [] };
    case 'EDITED': {
      const campos = camposEditados(e);
      return campos.length > 0
        ? edicionEnPalabras(quien, campos, aplico)
        : { titulo: `${quien} cambió algo${aplico}`, lineas: [] };
    }
    case 'TURNED_ON':
      return { titulo: `${quien} la encendió${aplico}`, lineas: [] };
    case 'TURNED_OFF':
      return { titulo: `${quien} la apagó${aplico}`, lineas: [] };
    case 'DELETED':
      return { titulo: `${quien} la borró${aplico}`, lineas: [] };
    case 'DRAFT_DISCARDED': {
      const d = detalleDe(e);
      const que = queCambiaba(d);
      // Mini-ronda del servidor: borrar una nueva sin aplicar o dejar la regla como
      // rige («volver atrás») son descartes de hecho; borrar una con un cambio sin
      // aplicar lo descarta (ronda final).
      if (d.descarte === 'BORRAR_NUEVA')
        return {
          titulo: 'Se borró antes de aplicarla: nunca rigió.',
          lineas: [`Lo hizo ${quienLoHizo(e)}.`],
        };
      if (d.descarte === 'VOLVER_ATRAS')
        return {
          titulo: 'Se dejó como rige hoy: el cambio sin aplicar se descartó.',
          lineas: [`Lo hizo ${quienLoHizo(e)}.`, ...(que ? [que] : [])],
        };
      // Ronda del QA de R4: marcar «Borrar» no la borra (sigue rigiendo hasta
      // aplicar; si después se descarta el borrado, nunca se borró). La que se
      // borró de verdad es la entrada DELETED, al aplicar.
      if (d.descarte === 'BORRAR_VIGENTE')
        return {
          titulo: 'Se marcó para borrar: su cambio sin aplicar se descartó.',
          lineas: [`Lo hizo ${quienLoHizo(e)}.`, ...(que ? [que] : [])],
        };
      return {
        titulo: 'Se descartó un cambio sin aplicar',
        lineas: [`Lo descartó ${quienLoHizo(e)}.`, ...(que ? [que] : [])],
      };
    }
    default:
      return { titulo: `${quien} cambió algo${aplico}`, lineas: [] };
  }
}
